import React, { Suspense, useEffect, useContext, useState } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import { Global } from '@emotion/react';
import { Box, Spinner, theme } from '@xometry/xometry_loft';
import * as Sentry from '@sentry/react';
import { FullStory } from '@fullstory/browser';

import BuyerLoginPage from './containers/BuyerLogin';
import BuyerRegistration from './containers/BuyerRegistration';
import CompleteRegistration from './containers/BuyerRegistration/CompleteRegistration';
import { RedirectToPartnerOnboarding } from './containers/Registration/SupplierRegistration';
import SupplierCapabilities from './containers/Registration/Capabilities';
import ShopFinancesRegistration from './containers/Registration/ShopFinancesRegistration';
import XACRegistration from './containers/Registration/XACRegistration';
import Account from './containers/Account';
import { ULPForgotPassword } from './containers/ForgotPassword/ULPForgotPassword';
import { ULPResetPassword } from './containers/ResetPassword/ULPResetPassword';
import TaxExemption from './containers/TaxExemption';
import SupplierAccount from './containers/SupplierAccount';
import PrivateRoute from './components/PrivateRoute';
import StaffRoute from './components/StaffRoute';
import UnifiedRegistration from './containers/Registration/UnifiedRegistration';
import CreditTermsApplication from './containers/BillingProfiles/CreditTermsApplication';
import AcceptInvite from './containers/Organization/AcceptInvite';
import RfqResponse from './containers/RfqResponse';
import { GET_ORIGIN, WORKCENTER_ORIGIN, SENTRY_DSN } from './config';
import { getPrimaryPartnerId } from './utils/helpers';
import TeamSettings from './containers/Organization/TeamSettings';
import CreateOrganization from './containers/Organization/CreateOrganization';
import Auth from '@xometry/auth-context';
import Navigation from './containers/Navigation';
import NavigationFooter from './containers/NavigationFooter';
import LoftShadowProvider from './shared/styles/LoftShadowProvider';
import { NewQuoteModal } from './components/NewQuoteModal/NewQuoteModal';
import { XomStatsigProvider } from './providers/statsig';
import { CoralogixProvider } from './shared/contexts/CoralogixContext';

declare global {
  interface Window {
    analytics?: {
      track: (event: string, properties?: Record<string, unknown>) => void;
      identify: (userId: string, traits?: Record<string, unknown>) => void;
      page: (name?: string, properties?: Record<string, unknown>) => void;
    };
  }
}

export enum ModalType {
  NEW_QUOTE_MODAL,
}

const RedirectPath = (props: { url: string }) => {
  const { url } = props;
  useEffect(() => window.location.assign(url), []);
  return <div />;
};

const RedirectTo404 = () => {
  const location = useLocation();
  if (location.pathname !== '/') {
    window.location.assign(`${GET_ORIGIN}/404`);
  }
  return null;
};

const Component = () => {
  const { user, isLoading } = useContext(Auth);

  const [openNewQuoteModal, setOpenNewQuoteModal] = useState<ModalType | null>(
    null
  );

  if (SENTRY_DSN) {
    Sentry.setUser({ id: user?.id, email: user?.emailAddress });
    FullStory('setIdentity', {
      uid: user?.id,
      properties: {
        displayName: `${user?.firstName} ${user?.lastName}`,
        email: user?.emailAddress,
        // Add your own custom user variables here, details at
        // https://developer.fullstory.com/browser/identification/set-user-properties/
        // reviewsWritten: 14
      },
    });
  }

  if (isLoading) {
    return (
      <Spinner
        scale="full-screen"
        style={{
          position: 'absolute',
          left: 'calc(50% - 70px)',
          top: '40%',
        }}
      />
    );
  }

  // list of paths to exclude nav and footer
  const excludeHeaderFooterPaths = [
    'account/register-shop-finances',
    'account/register-xac',
    'account/register',
    'account/ulp-forgot-password',
    'account/ulp-reset-password',
  ];

  // returns true if the url matches the exclude paths
  const excludeHeaderFooter = excludeHeaderFooterPaths.some((path) =>
    window.location.pathname.includes(path)
  );

  return (
    <CoralogixProvider user={user}>
      <XomStatsigProvider
        userId={user?.id}
        partnerId={getPrimaryPartnerId(user)}
      >
        <Global
          styles={`
            html {
                background-color: ${theme.colors.darkBlues[5]};

                dialog {
                    & button[slot="close"] {
                         font-family: var(--l-font-family-icons);
                         font-size: var(--l-font-size-medium);
                    }
                }
            }
        `}
        />
        <LoftShadowProvider sticky>
          {!excludeHeaderFooter && (
            <Navigation setOpenNewQuoteModal={setOpenNewQuoteModal} />
          )}
        </LoftShadowProvider>
        {!excludeHeaderFooter && (
          <NewQuoteModal
            openModal={openNewQuoteModal}
            setOpenModal={setOpenNewQuoteModal}
            activeUserId={user?.id as string}
          />
        )}
        <Router>
          <Suspense fallback={<div />}>
            <Routes>
              <Route
                path="/user-settings/:userid"
                element={
                  <StaffRoute
                    component={Account}
                    pageName="User Settings For User"
                  />
                }
              />
              <Route
                path="/user-settings"
                element={
                  <PrivateRoute component={Account} pageName="User Settings" />
                }
              />
              <Route
                path="/add-tax-exemption/:userid"
                element={
                  <StaffRoute
                    component={TaxExemption}
                    pageName="Add Tax Exemption For User"
                  />
                }
              />
              <Route
                path="/add-tax-exemption"
                element={
                  <PrivateRoute
                    component={TaxExemption}
                    pageName="Add Tax Exemption"
                  />
                }
              />
              <Route
                path="/account/change-password"
                element={
                  <PrivateRoute
                    component={() => {
                      window.location.replace('/account/ulp-forgot-password');
                    }}
                    pageName="Change Password"
                  />
                }
              />
              <Route
                // DEPRECATED: under the hood BuyerLoginPage now redirects to the Auth0 /login page
                path="/account/buyer/login"
                element={<BuyerLoginPage />}
              />
              <Route
                path="/account/buyer/register"
                element={<BuyerRegistration />}
              />
              <Route
                path="/account/buyer/complete-registration"
                element={<CompleteRegistration />}
              />
              <Route
                // DEPRECATED: route retained temporarily to redirect legacy, should be removed once full migration the query param method is complete.
                path="/account/buyer/complete-registration/:temporaryKey"
                element={<CompleteRegistration />}
              />
              <Route
                path="/account/organization/invite"
                element={
                  <PrivateRoute
                    component={AcceptInvite}
                    pageName="Accept Organization Invite"
                  />
                }
              />
              <Route
                path="/account/organization/create"
                element={
                  <PrivateRoute
                    component={CreateOrganization}
                    pageName="Create Organization"
                  />
                }
              />
              <Route
                path="/account/organization/:orgId/settings"
                element={
                  <PrivateRoute
                    component={TeamSettings}
                    pageName="Organization Settings"
                  />
                }
              />
              <Route
                path="/account/register-supplier"
                element={<RedirectToPartnerOnboarding />}
              />
              <Route
                path="/account/apply-for-terms/:userid"
                element={
                  <StaffRoute
                    component={CreditTermsApplication}
                    pageName="Credit Terms Application for User"
                  />
                }
              />
              <Route
                path="/account/apply-for-terms"
                element={
                  <PrivateRoute
                    component={CreditTermsApplication}
                    pageName="Credit Terms Application"
                  />
                }
              />
              <Route
                path="/account/supplier/capabilities"
                element={
                  <PrivateRoute
                    component={SupplierCapabilities}
                    pageName="Supplier Capabilities"
                  />
                }
              />
              <Route
                path="account/supplier/account/:userId"
                element={
                  <StaffRoute
                    component={SupplierAccount}
                    pageName="Supplier Account"
                  />
                }
              />
              <Route
                path="account/supplier/finishes/:userId"
                element={
                  <StaffRoute
                    component={SupplierAccount}
                    pageName="Supplier Finishes"
                  />
                }
              />
              <Route
                path="account/supplier/materials/:userId"
                element={
                  <StaffRoute
                    component={SupplierAccount}
                    pageName="Supplier Materials"
                  />
                }
              />
              <Route
                path="account/supplier/machines/:userId"
                element={
                  <StaffRoute
                    component={SupplierAccount}
                    pageName="Supplier Machines"
                  />
                }
              />
              <Route
                path="account/supplier/certifications/:userId"
                element={
                  <StaffRoute
                    component={SupplierAccount}
                    pageName="Supplier Certifications"
                  />
                }
              />
              {/* route partner to workcenter */}
              <Route
                path="/account/supplier/onboarding"
                element={
                  <RedirectPath url={`${WORKCENTER_ORIGIN}/home/main`} />
                }
              />
              <Route
                path="/account/supplier/account"
                element={
                  <RedirectPath
                    url={`${WORKCENTER_ORIGIN}/xometry-settings/account`}
                  />
                }
              />
              <Route
                path="/account/supplier/finishes"
                element={
                  <RedirectPath
                    url={`${WORKCENTER_ORIGIN}/xometry-settings/finishes`}
                  />
                }
              />
              <Route
                path="/account/supplier/materials"
                element={
                  <RedirectPath
                    url={`${WORKCENTER_ORIGIN}/xometry-settings/materials`}
                  />
                }
              />
              <Route
                path="/account/supplier/certifications"
                element={
                  <RedirectPath
                    url={`${WORKCENTER_ORIGIN}/xometry-settings/certifications`}
                  />
                }
              />
              <Route
                path="/account/supplier/machines"
                element={
                  <RedirectPath
                    url={`${WORKCENTER_ORIGIN}/xometry-settings/machines`}
                  />
                }
              />
              <Route
                path="/account/supplier/capabilities"
                element={
                  <RedirectPath
                    url={`${WORKCENTER_ORIGIN}/xometry-settings/capabilities`}
                  />
                }
              />
              <Route
                path="/account/supplier/*"
                element={
                  <RedirectPath
                    url={`${WORKCENTER_ORIGIN}/xometry-settings/account`}
                  />
                }
              />
              <Route
                path="/account/forgot-password"
                element={
                  <PrivateRoute
                    component={() => {
                      window.location.replace('/account/ulp-forgot-password');
                    }}
                    pageName="OldForgotPassword"
                  ></PrivateRoute>
                }
              />
              <Route
                path="/account/ulp-forgot-password"
                element={<ULPForgotPassword />}
              />
              <Route
                path="/account/ulp-reset-password"
                element={<ULPResetPassword />}
              />
              <Route
                path="/account/reset-password/*"
                element={
                  <PrivateRoute
                    component={() => {
                      window.location.replace('/account/ulp-forgot-password');
                    }}
                    pageName="OldResetPassword"
                  ></PrivateRoute>
                }
              />
              <Route
                path="/account/register"
                element={<UnifiedRegistration />}
              />
              <Route
                element={
                  <PrivateRoute
                    component={ShopFinancesRegistration}
                    pageName="Register Shop Finances"
                  />
                }
                path="/account/register-shop-finances"
              />
              <Route
                element={
                  <PrivateRoute
                    component={XACRegistration}
                    pageName="Register Xometry Advance Card"
                  />
                }
                path="/account/register-xac"
              />
              <Route
                path="/rfq-responses/:rfqId"
                element={
                  <PrivateRoute
                    component={RfqResponse}
                    pageName="RFQ Response"
                  />
                }
              />
              <Route
                path="/supplier/onboarding"
                element={<RedirectPath url={`/account/supplier/onboarding`} />}
              />
              {/* No matching route */}
              <Route path="*" element={<RedirectTo404 />} />
            </Routes>
          </Suspense>
        </Router>
        {!excludeHeaderFooter && (
          <Box zIndex="-1">
            <LoftShadowProvider>
              <NavigationFooter />
            </LoftShadowProvider>
          </Box>
        )}
      </XomStatsigProvider>
    </CoralogixProvider>
  );
};

export default Component;
